<script lang="ts">
	export let label: string;
	export let deviceName: string = '';

	type SentenceParts = {
		prefix: string;
		device: string;
		property: string;
		keyword: string;
		value: string;
		valueTone: 'positive' | 'negative' | 'numeric' | 'neutral';
	};

	const KEYWORD_PATTERN =
		/\b(no longer detects|detects|is less than|is more than|is below|is above|is at most|is at least|is not|is|becomes|changes to|matches)\b/i;

	$: parts = parseSentence(label, deviceName);

	function parseSentence(text: string, device: string): SentenceParts {
		const withoutPrefix = text.replace(/^when\s+/i, '').trim();
		const deviceIndex = device
			? withoutPrefix.toLocaleLowerCase().indexOf(device.toLocaleLowerCase())
			: -1;

		if (deviceIndex < 0) {
			return {
				prefix: '',
				device: '',
				property: text,
				keyword: '',
				value: '',
				valueTone: 'neutral',
			};
		}

		const predicate = withoutPrefix.slice(deviceIndex + device.length).trim();
		const keywordMatch = predicate.match(KEYWORD_PATTERN);
		const keyword = keywordMatch?.[0] ?? '';
		const property = keywordMatch ? predicate.slice(0, keywordMatch.index).trim() : predicate;
		const value = keywordMatch
			? predicate.slice((keywordMatch.index ?? 0) + keyword.length).trim()
			: '';
		const semanticValue = `${keyword} ${value}`.toLowerCase();

		return {
			prefix: 'When',
			device,
			property,
			keyword,
			value,
			valueTone: /\b(off|closed|inactive|not detected|no longer)\b/.test(semanticValue)
				? 'negative'
				: /\b(on|open|active|detected|detects|pressed|presence)\b/.test(semanticValue)
					? 'positive'
					: /\d/.test(value)
						? 'numeric'
						: 'neutral',
		};
	}
</script>

<span class="condition-sentence">
	{#if parts.device}
		<span class="prefix">{parts.prefix}</span>
		<span class="device">{parts.device}</span>
		{#if parts.property}<span class="property">{parts.property}</span>{/if}
		{#if parts.keyword}<span class="keyword tone-{parts.valueTone}">{parts.keyword}</span>{/if}
		{#if parts.value}<span class="value tone-{parts.valueTone}">{parts.value}</span>{/if}
	{:else}
		<span class="property fallback">{parts.property}</span>
	{/if}
</span>

<style>
	.condition-sentence {
		display: flex;
		align-items: baseline;
		flex-wrap: wrap;
		column-gap: 0.3em;
		row-gap: 0.12em;
		font-size: 0.8rem;
		line-height: 1.4;
		color: var(--color-text-secondary, #475569);
	}

	.prefix {
		color: var(--color-text-3, #64748b);
		font-weight: 500;
	}

	.device {
		color: var(--color-primary, #2f7df6);
		font-weight: 750;
	}

	.property {
		color: var(--color-text-primary, #111827);
		font-weight: 550;
	}

	.keyword {
		color: #7c3aed;
		font-weight: 700;
	}

	.value {
		font-weight: 750;
		color: var(--color-text-primary, #111827);
	}

	.keyword.tone-positive,
	.value.tone-positive {
		color: #4ade80;
	}

	.keyword.tone-negative,
	.value.tone-negative {
		color: #fb7185;
	}

	.value.tone-numeric {
		color: #fbbf24;
	}

	.fallback {
		line-height: 1.4;
	}
</style>
